import React, { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Loader2,
  RefreshCw,
  ClipboardCheck,
  ShieldCheck,
  Activity,
  Home,
  FileCheck2,
  Sparkles,
  Store,
  Award,
  Briefcase,
  MapPin,
  BadgeCheck,
  Banknote,
  Landmark,
  Calendar,
  Users,
  Phone,
  IdCard,
  MapPinned,
  Contact2,
} from "lucide-react";

type OtherDetails = {
  id?: number | string;

  // assessment
  stock_neat?: any;
  accurate_ledger?: any;
  sales_activity?: any;
  permanent?: any;
  proof_ownership?: any;
  fourth_coming?: any;
  known_market?: any;
  sound_reputation?: any;

  // employment
  employer?: any;
  employerAddress?: any;
  employeeStatus?: any;
  employeeNetIncome?: any;
  employeePlot?: any;
  employerTown?: any;
  employedYear?: any;

  // kin
  kinName?: any;
  KINR?: any;
  kinIdNumber?: any;
  kinAddress?: any;
  kinEstate?: any;
  kinTelephone?: any;
  KinLandMark?: any;
  kinTown?:any


  // optional
  image?: any;
};

type AltPhone = { id?: number | string; phone_number?: string };

export default function CustomerOtherDetailsTab({ customerId }: { customerId: string }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [details, setDetails] = useState<OtherDetails | null>(null);
  const [phones, setPhones] = useState<AltPhone[]>([]);

  function safe(v: any) {
    if (v === null || v === undefined) return "";
    const s = String(v).trim();
    if (!s || s === "0" || s.toLowerCase() === "null") return "";
    return s;
  }

  function isYesLike(v: any) {
    const s = safe(v).toLowerCase();
    return ["yes", "true", "1", "ok", "good"].includes(s);
  }

  function ValueBadge({ value }: { value: any }) {
    const s = safe(value);
    if (!s) return <span className="text-muted-foreground">—</span>;

    const ok = isYesLike(s);
    return (
      <Badge
        className={cn(
          "border px-2.5 py-1 rounded-full text-xs font-semibold",
          ok
            ? "bg-emerald-500/10 text-emerald-700 border-emerald-200"
            : "bg-primary/10 text-primary border-primary/20"
        )}
      >
        {ok ? (
          <span className="inline-flex items-center gap-1">
            <BadgeCheck className="h-3.5 w-3.5" />
            {s}
          </span>
        ) : (
          s
        )}
      </Badge>
    );
  }

  function MetricLine({
    icon: Icon,
    label,
    value,
  }: {
    icon: any;
    label: string;
    value: any;
  }) {
    // For assessment we show even if empty -> render dash
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border bg-muted/20 p-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="h-4 w-4 text-primary shrink-0" />
          <div className="text-sm font-medium text-foreground/80 truncate">{label}</div>
        </div>
        <ValueBadge value={value} />
      </div>
    );
  }

  function InfoLine({
    icon: Icon,
    label,
    value,
  }: {
    icon: any;
    label: string;
    value: any;
  }) {
    const s = safe(value);
    if (!s) return null;

    return (
      <div className="flex items-start gap-2">
        <div className="h-8 w-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-muted-foreground font-medium">{label}</div>
          <div className="text-sm font-semibold break-words">{s}</div>
        </div>
      </div>
    );
  }

  async function load() {
  if (!customerId) return;

  setLoading(true);
  setErr("");

  try {
    // ⚠️ make sure this matches your Laravel route
    const res = await apiFetch<any>(`/customer-profile/${customerId}/others`);


    const payload = res?.data ?? res;
    const root = payload?.data && (payload?.phone_numbers !== undefined) ? payload : payload?.data ?? payload;

    const customer = root?.data ?? root ?? null;
setPhones(res?.phone_numbers ?? []);
    setDetails(customer);
    
  } catch (e: any) {
    setErr(e?.message || "Failed to load other details");
    setDetails(null);
    setPhones([]);
  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const hasEmployment = useMemo(() => {
    const d = details;
    if (!d) return false;
    return Boolean(
      safe(d.employer) ||
        safe(d.employerAddress) ||
        safe(d.employeeStatus) ||
        safe(d.employeeNetIncome) ||
        safe(d.employeePlot) ||
        safe(d.employerTown) ||
        safe(d.employedYear)
    );
  }, [details]);

  const hasKin = useMemo(() => {
    const d = details;
    if (!d) return false;
    return Boolean(
      safe(d.kinName) ||
        safe(d.KINR) ||
        safe(d.kinIdNumber) ||
        safe(d.kinAddress) ||
        safe(d.kinEstate) ||
        safe(d.kinTelephone) ||
        safe(d.kinTown) ||
        safe(d.KinLandMark)
    );
  }, [details]);

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-6 text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading other details...
        </CardContent>
      </Card>
    );
  }

  if (err) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-4 space-y-3">
          <div className="text-sm text-destructive">{err}</div>
          <Button variant="secondary" onClick={load} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!details) {
    return (
      <Card className="shadow-card">
        <CardContent className="p-4 text-sm text-muted-foreground">
          No other details found for this customer.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4">
      {/* 1) Assessment (always show) */}
      <Card className="shadow-card xl:col-span-3">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-primary inline-flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" />
              Assessment
            </div>
            <Badge className="bg-muted text-foreground border">Info</Badge>
          </div>

          <div className="space-y-2">
            <MetricLine icon={Sparkles} label="Stock Neat" value={details.stock_neat} />
            <MetricLine icon={Landmark} label="Accurate Ledger" value={details.accurate_ledger} />
            <MetricLine icon={Activity} label="Sales Activity" value={details.sales_activity} />
            <MetricLine icon={Home} label="Permanent" value={details.permanent} />
            <MetricLine icon={FileCheck2} label="Proof Ownership" value={details.proof_ownership} />
            <MetricLine icon={ShieldCheck} label="Forth Coming" value={details.fourth_coming} />
            <MetricLine icon={Store} label="Known Market" value={details.known_market} />
            <MetricLine icon={Award} label="Sound Reputation" value={details.sound_reputation} />
          </div>
        </CardContent>
      </Card>

      {/* 2) Employment (show only if any field exists) */}
      {hasEmployment ? (
        <Card className="shadow-card xl:col-span-3">
          <CardContent className="p-4 space-y-4">
            <div className="text-sm font-semibold text-primary inline-flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Employment
            </div>

            <div className="space-y-3">
              <InfoLine icon={Briefcase} label="Employer Name" value={details.employer} />
              <InfoLine icon={MapPin} label="Address" value={details.employerAddress} />
              <InfoLine icon={BadgeCheck} label="Employee Status" value={details.employeeStatus} />
              <InfoLine icon={Banknote} label="Net Income" value={details.employeeNetIncome} />
              <InfoLine icon={Landmark} label="Employee Plot" value={details.employeePlot} />
              <InfoLine icon={MapPinned} label="Employer Town" value={details.employerTown} />
              <InfoLine icon={Calendar} label="Employed Year" value={details.employedYear} />
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="shadow-card xl:col-span-3">
          <CardContent className="p-4">
            <div className="text-sm font-semibold text-primary inline-flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Employment
            </div>
            <div className="mt-3 text-sm text-muted-foreground">No employment details.</div>
          </CardContent>
        </Card>
      )}

      {/* 3) Next of Kin (show card even if only relationship exists like your sample) */}
      <Card className="shadow-card xl:col-span-3">
        <CardContent className="p-4 space-y-4">
          <div className="text-sm font-semibold text-primary inline-flex items-center gap-2">
            <Users className="h-4 w-4" />
            Next of Kin
          </div>

          {hasKin ? (
            <div className="space-y-3">
              <InfoLine icon={Contact2} label="Kin Name" value={details.kinName} />
              <InfoLine icon={Users} label="Relationship" value={details.KINR} />
              <InfoLine icon={IdCard} label="ID Number" value={details.kinIdNumber} />
              <InfoLine icon={MapPin} label="Address" value={details.kinAddress} />
              <InfoLine icon={Home} label="Estate" value={details.kinEstate} />
              <InfoLine icon={Phone} label="Phone Number" value={details.kinTelephone} />
              <InfoLine icon={MapPinned} label="Landmark" value={details.KinLandMark} />
              <InfoLine icon={MapPinned} label="Town" value={details.kinTown} />
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No next of kin details.</div>
          )}
        </CardContent>
      </Card>

      {/* 4) Alternative Phone Numbers */}
      <Card className="shadow-card xl:col-span-3">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-primary inline-flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Alternative Numbers
            </div>
            <Badge className="bg-primary/10 text-primary border border-primary/20">
              {phones.length}
            </Badge>
          </div>

          {phones.length ? (
            <div className="space-y-2">
              {phones.map((p, idx) => (
                <div
                  key={String(p.id ?? idx)}
                  className="rounded-xl border bg-muted/20 px-3 py-2 flex items-center justify-between"
                >
                  <div className="text-xs text-muted-foreground">#{idx + 1}</div>
                  <div className="text-sm font-semibold">{safe(p.phone_number) || "-"}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No alternative phone numbers.</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}